// src/components/kuestion/campus/AssignmentNew.jsx
//
// Template + mode + optional window + students (contract §6). Mode belongs
// to the assignment, not the template. The window gates starting, not
// finishing — the copy says so because examiners ask.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Alert, Note } from '../shared/Bits';
import { FileIcon, SearchIcon, ClockIcon } from '../shared/Icons';
import { MODE, fullName, fromLocalInput, zoneLabel } from '../shared/format';
import { kq } from '../../../config/routes';

export default function AssignmentNew() {
  const navigate = useNavigate();
  const { api, templates, reloadTemplates, students, reloadAssignments } = useCampus();

  const [template, setTemplate] = useState(null);
  const [picking, setPicking] = useState(false);
  const [mode, setMode] = useState(MODE.PROCTORED);
  const [opens, setOpens] = useState('');
  const [closes, setCloses] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!templates.length) reloadTemplates(); }, [templates.length, reloadTemplates]);

  // Everyone active is selected by default — assigning to the whole class is
  // the common case; trimming is the exception.
  useEffect(() => {
    setSelected(new Set(students.filter((s) => s.is_active !== false).map((s) => s.id)));
  }, [students]);

  const visible = useMemo(() => {
    const n = q.trim().toLowerCase();
    return students.filter((s) => !n || `${fullName(s)} ${s.login_id || ''}`.toLowerCase().includes(n));
  }, [students, q]);

  const toggle = (id) => setSelected((set) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const setAll = (ids) => setSelected(new Set(ids));

  const windowBad = opens && closes && new Date(closes) <= new Date(opens);
  const canSubmit = template && selected.size > 0 && !windowBad && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError('');
    try {
      await api.postJson(API_ENDPOINTS.CAMPUS_ASSIGNMENTS, {
        template_id: template.id,
        // The schema requires a title; until the form has its own field the
        // template's name is the honest default.
        title: template.template_name || template.name,
        student_ids: [...selected],
        mode,
        opens_at: fromLocalInput(opens),
        closes_at: fromLocalInput(closes),
      });
      await reloadAssignments();
      navigate(kq('assignments'));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="kqc-page kqc-page--flush">
      <div className="kqc-split">
        <div className="kqc-split__main">
          <button type="button" className="btn btn-ghost kqc-back" onClick={() => navigate(kq('assignments'))}>&larr; Assignments</button>
          <h1 className="kqc-h1" style={{ marginBottom: 8 }}>Assign an exam</h1>
          <p className="kqc-lede" style={{ marginBottom: 26, maxWidth: '56ch' }}>Mode belongs to the assignment, not the template — the same paper can be a formal sitting for one class and open practice for another.</p>

          {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}

          <div className="kqc-kicker" style={{ marginBottom: 10 }}>1 · Template</div>
          {template && !picking ? (
            <div className="kqc-tplpick" style={{ marginBottom: 26 }}>
              <span className="i"><FileIcon /></span>
              <span className="kqc-tplpick__body">
                <strong>{template.template_name || template.exam_name}</strong>
                <em>{template.total_questions} questions · {template.time_limit} minutes{template.total_score ? ` · ${template.total_score} marks` : ''} · {template.pass_percentage != null ? `${template.pass_percentage}% to pass` : 'no pass mark'}</em>
              </span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPicking(true)}>Change</button>
            </div>
          ) : (
            <div style={{ marginBottom: 26 }}>
              {templates.length === 0 ? (
                <div className="kqc-empty">No templates yet. <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 0, display: 'inline-flex' }} onClick={() => navigate(kq('templates'))}>Build one first &rarr;</button></div>
              ) : (
                <div className="kqc-tpllist">
                  {templates.map((t) => (
                    <button type="button" key={t.id} className={template && template.id === t.id ? 'is-active' : ''} onClick={() => { setTemplate(t); setPicking(false); }}>
                      <span style={{ display: 'flex', width: 22, height: 22, color: 'var(--color-accent)', flex: '0 0 auto' }}><FileIcon /></span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <strong style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: 14 }}>{t.template_name || t.exam_name}</strong>
                        <em style={{ display: 'block', fontStyle: 'normal', fontSize: 12, color: 'var(--color-neutral-700)' }}>{t.total_questions} questions · {t.time_limit} minutes</em>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="kqc-kicker" style={{ marginBottom: 10 }}>2 · Mode</div>
          <div className="kqc-modes" style={{ marginBottom: 26 }}>
            <button type="button" className={`kqc-mode${mode === MODE.PROCTORED ? ' is-active' : ''}`} onClick={() => setMode(MODE.PROCTORED)} aria-pressed={mode === MODE.PROCTORED}>
              <span className="kqc-mode__top"><span className="kqc-mode__dot" /><strong>Proctored</strong></span>
              <span className="kqc-mode__lede">A formal sitting. Use this when the mark counts.</span>
              <span className="kqc-mode__rows">
                <span><strong className="kqc-accent-ink">One sitting only</strong> — no retakes, ever</span>
                <span>No score shown on submit</span>
                <span>No answers, no explanations, no dashboard</span>
              </span>
            </button>
            <button type="button" className={`kqc-mode${mode === MODE.SELF ? ' is-active' : ''}`} onClick={() => setMode(MODE.SELF)} aria-pressed={mode === MODE.SELF}>
              <span className="kqc-mode__top"><span className="kqc-mode__dot" /><strong>Self-assessment</strong></span>
              <span className="kqc-mode__lede">Practice. Use this to let students find their own gaps.</span>
              <span className="kqc-mode__rows">
                <span>Unlimited retakes</span>
                <span>Score, wrong answers and explanations</span>
                <span>Feeds their own topic dashboard</span>
              </span>
            </button>
          </div>

          <div className="kqc-kicker" style={{ marginBottom: 10 }}>3 · Window <span className="kqc-muted" style={{ letterSpacing: 0, textTransform: 'none', fontWeight: 400, fontSize: 12 }}>— both optional</span></div>
          <div className="kqc-row kqc-row--gap" style={{ alignItems: 'stretch', marginBottom: 12 }}>
            <div className="field" style={{ flex: '1 1 190px' }}><label htmlFor="opens">Opens at</label><input id="opens" className="input" type="datetime-local" value={opens} onChange={(e) => setOpens(e.target.value)} /></div>
            <div className="field" style={{ flex: '1 1 190px' }}>
              <label htmlFor="closes">Closes at</label>
              <input id="closes" className="input" type="datetime-local" value={closes} onChange={(e) => setCloses(e.target.value)} />
              <div className="kqc-tiny" style={{ marginTop: 5, textTransform: 'none', letterSpacing: 0 }}>Your time{zoneLabel() ? ` (${zoneLabel()})` : ''}. Students see it in their own zone, named.</div>
            </div>
          </div>
          {windowBad && <div className="kqc-small kqc-accent-ink" style={{ marginBottom: 12 }}>The closing time must be after the opening time.</div>}
          <Note icon={<ClockIcon />} style={{ marginBottom: 26 }}>The window gates <strong>starting</strong>, not finishing. A student already sitting when it closes is not interrupted — they finish and submit normally. Leave both blank to make it available now and indefinitely.</Note>

          <div className="kqc-row">
            <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} disabled={!canSubmit} onClick={submit}>{busy ? 'Assigning…' : `Assign to ${selected.size} student${selected.size === 1 ? '' : 's'}`}</button>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => navigate(kq('assignments'))} disabled={busy}>Cancel</button>
          </div>
        </div>

        <div className="kqc-split__side kqc-split__side--right">
          <div className="kqc-split__head">
            <div className="kqc-row" style={{ marginBottom: 10 }}>
              <span className="kqc-kicker">4 · Students</span>
              <span className="kqc-spacer" />
              <span className="kqc-count kqc-count--accent">{selected.size} selected</span>
            </div>
            <div className="kqc-search" style={{ marginBottom: 10 }}>
              <span><SearchIcon /></span>
              <input type="text" placeholder="Search the roster" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="kqc-segs">
              <button type="button" className={selected.size === students.length && students.length ? 'is-active' : ''} onClick={() => setAll(students.map((s) => s.id))}>All {students.length}</button>
              <button type="button" className={selected.size === 0 ? 'is-active' : ''} onClick={() => setAll([])}>None</button>
              <button type="button" onClick={() => setAll(students.filter((s) => s.is_active !== false).map((s) => s.id))}>Active only</button>
            </div>
          </div>
          <div className="kqc-split__scroll">
            {visible.length === 0 ? <div className="kqc-small kqc-muted" style={{ padding: '20px' }}>{students.length ? 'No students match.' : 'No students yet.'}</div>
              : visible.map((s) => (
                <label key={s.id} className="kqc-pick">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  <span>
                    <strong>{fullName(s)}{s.is_active === false ? ' · disabled' : ''}</strong>
                    <em>{s.login_id}</em>
                  </span>
                </label>
              ))}
          </div>
          <div className="kqc-split__foot">Showing {visible.length} of {students.length}</div>
        </div>
      </div>
    </div>
  );
}
